import { useEffect, useState } from 'react'
import SunCalc from 'suncalc'

/**
 * SunCalc-driven day/night theme (MAP-04).
 *
 * Research §Code Examples Pattern 5 (lines 482-529). Decisions:
 *   - D-67: use actual sunrise/sunset (not wall-clock) — Romania is dark
 *     by 16:30 in winter; the map should be dark then, not at a fixed hour.
 *   - D-68: Bucharest fallback when lat/lng are null.
 *   - D-69: single `setTimeout` scheduled for the next crossing (no polling).
 *   - D-70: no manual override in Phase 2.
 *
 * Side effects on theme change:
 *   - writes `document.documentElement[data-theme]` (tokens.css swap target)
 *   - updates the `<meta name="theme-color">` tag so the mobile browser
 *     chrome colour follows (Pitfall 6, lines 755-763).
 */

export type Theme = 'day' | 'night'

const BUCHAREST = { lat: 44.4268, lng: 26.1025 }
const MS_PER_DAY = 24 * 60 * 60 * 1000
const MIN_TIMER_MS = 60_000 // guard against 0-ms loops if clocks drift

export function computeTheme(now: Date, lat: number, lng: number): Theme {
  const t = SunCalc.getTimes(now, lat, lng)
  return now >= t.sunrise && now < t.sunset ? 'day' : 'night'
}

/**
 * The next future Date at which the theme will flip.
 *   - Before today's sunrise → today's sunrise.
 *   - After sunrise but before sunset → today's sunset.
 *   - After sunset → tomorrow's sunrise.
 */
export function nextCrossing(now: Date, lat: number, lng: number): Date {
  const today = SunCalc.getTimes(now, lat, lng)
  if (now < today.sunrise) return today.sunrise
  if (now < today.sunset) return today.sunset
  const tomorrow = new Date(now.getTime() + MS_PER_DAY)
  return SunCalc.getTimes(tomorrow, lat, lng).sunrise
}

/**
 * React hook: derive + hold the current Theme, schedule the next flip, sync
 * `data-theme` and `<meta name="theme-color">` on every change.
 *
 * Passing `(null, null)` falls back to Bucharest (D-68).
 */
export function useTheme(
  fixLat: number | null,
  fixLng: number | null,
): Theme {
  const lat = fixLat ?? BUCHAREST.lat
  const lng = fixLng ?? BUCHAREST.lng

  const [theme, setTheme] = useState<Theme>(() =>
    computeTheme(new Date(), lat, lng),
  )

  // Recompute + reschedule whenever the (lat, lng) dependency pair changes.
  useEffect(() => {
    const now = new Date()
    setTheme(computeTheme(now, lat, lng))
    const crossing = nextCrossing(now, lat, lng)
    const ms = Math.max(MIN_TIMER_MS, crossing.getTime() - Date.now())
    const timer = setTimeout(() => {
      setTheme(computeTheme(new Date(), lat, lng))
    }, ms)
    return () => clearTimeout(timer)
  }, [lat, lng])

  // Push the theme into the DOM + the mobile-chrome meta tag.
  useEffect(() => {
    if (typeof document === 'undefined') return
    document.documentElement.setAttribute('data-theme', theme)
    const meta = document.querySelector<HTMLMetaElement>(
      'meta[name="theme-color"]',
    )
    meta?.setAttribute('content', theme === 'night' ? '#0F1419' : '#FFFFFF')
  }, [theme])

  return theme
}
